import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { inboxAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Inbox, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

export default function QuickCapture({ open, onClose }) {
  const { createNote, notes, updateNote, fetchNotebooks } = useApp();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mode, setMode] = useState('new'); // new | append-last | append-select
  const [appendNoteId, setAppendNoteId] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setContent('');
      setMode('new');
      setAppendNoteId('');
      setTimeout(() => contentRef.current?.focus(), 100);
    }
  }, [open]);

  const recentNotes = notes
    .filter((n) => !n.is_deleted && !n.is_archived)
    .slice(0, 10);

  const handleSave = useCallback(async () => {
    if (!content.trim() && !title.trim()) return;
    setSaving(true);
    try {
      if (mode === 'new') {
        // Ensure Inbox notebook exists
        const inboxRes = await inboxAPI.ensure();
        const inboxId = inboxRes.data.id;
        await fetchNotebooks();
        await createNote({
          title: title.trim() || '',
          content: content.trim(),
          notebook_id: inboxId,
        });
        toast.success('Saved to Inbox');
      } else if (mode === 'append-last' && recentNotes.length > 0) {
        const last = recentNotes[0];
        const separator = last.content?.trim() ? '\n\n---\n\n' : '';
        await updateNote(last.id, {
          content: (last.content || '') + separator + content.trim(),
        });
        toast.success(`Appended to "${last.title || 'Untitled'}"`);
      } else if (mode === 'append-select' && appendNoteId) {
        const target = notes.find((n) => n.id === appendNoteId);
        if (target) {
          const separator = target.content?.trim() ? '\n\n---\n\n' : '';
          await updateNote(target.id, {
            content: (target.content || '') + separator + content.trim(),
          });
          toast.success(`Appended to "${target.title || 'Untitled'}"`);
        }
      }
      onClose();
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  }, [mode, title, content, appendNoteId, recentNotes, notes, createNote, updateNote, fetchNotebooks, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="quick-capture-dialog">
        <DialogTitle className="sr-only">Quick Capture</DialogTitle>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-accent-amber" />
            <span className="text-sm font-medium">Quick Capture</span>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowModeMenu(!showModeMenu)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted/50 transition-colors"
              data-testid="quick-capture-mode-btn"
            >
              {mode === 'new' && 'New note'}
              {mode === 'append-last' && 'Append to last'}
              {mode === 'append-select' && 'Append to...'}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showModeMenu && (
              <div className="absolute right-0 top-full mt-1 w-44 rounded-lg border border-border bg-popover shadow-lg z-50" data-testid="quick-capture-mode-menu">
                {[
                  { id: 'new', label: 'New note (Inbox)' },
                  { id: 'append-last', label: 'Append to last note' },
                  { id: 'append-select', label: 'Append to specific note' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => { setMode(opt.id); setShowModeMenu(false); }}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                      mode === opt.id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                    data-testid={`quick-capture-mode-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-2" onKeyDown={handleKeyDown}>
          {mode === 'new' && (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)"
              className="w-full bg-transparent text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground/40"
              data-testid="quick-capture-title"
            />
          )}

          {mode === 'append-select' && (
            <select
              value={appendNoteId}
              onChange={(e) => setAppendNoteId(e.target.value)}
              className="w-full h-8 px-2 text-xs bg-input border border-border rounded outline-none"
              data-testid="quick-capture-note-select"
            >
              <option value="">Select a note...</option>
              {recentNotes.map((n) => (
                <option key={n.id} value={n.id}>{n.title || 'Untitled'}</option>
              ))}
            </select>
          )}

          <textarea
            ref={contentRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Type your thought..."
            rows={5}
            className="w-full bg-transparent text-sm text-foreground outline-none resize-none placeholder:text-muted-foreground/40 leading-relaxed"
            data-testid="quick-capture-content"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/30 bg-secondary/20">
          <span className="text-[10px] text-muted-foreground/50">Ctrl+Enter to save</span>
          <Button
            onClick={handleSave}
            size="sm"
            disabled={saving || (!content.trim() && !title.trim())}
            className="h-7 px-3 text-xs"
            data-testid="quick-capture-save-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
